import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { getLiveTvStreamPlaybackUrl, getLiveTvYouTubeEmbedUrl, shouldAttemptLiveTvStream } from '../services/liveTv.service';
import { LiveTvChannelWithStream } from '../types/liveTv.types';
import LiveTvPlayer from './LiveTvPlayer';

interface LiveTvPlaybackProps {
  channel: LiveTvChannelWithStream;
}

type PlaybackMode = 'stream' | 'youtube' | 'official_embed' | 'none';

function getInitialPlaybackMode(channel: LiveTvChannelWithStream): PlaybackMode {
  const youtubeEmbedUrl = getLiveTvYouTubeEmbedUrl(channel);

  if (channel.playbackType === 'youtube' && youtubeEmbedUrl) {
    return 'youtube';
  }

  if (shouldAttemptLiveTvStream(channel)) {
    return 'stream';
  }

  if (youtubeEmbedUrl) {
    return 'youtube';
  }

  if (channel.embedUrl) {
    return 'official_embed';
  }

  return 'none';
}

export default function LiveTvPlayback({ channel }: LiveTvPlaybackProps) {
  const [mode, setMode] = useState<PlaybackMode>(() => getInitialPlaybackMode(channel));

  useEffect(() => {
    setMode(getInitialPlaybackMode(channel));
  }, [
    channel.id,
    channel.playbackType,
    channel.stream?.url,
    channel.youtubeChannelId,
    channel.youtubeVideoId,
    channel.embedUrl,
  ]);

  const sources = useMemo(() => {
    const result: string[] = [];
    const streamUrl = getLiveTvStreamPlaybackUrl(channel);

    if (streamUrl) {
      result.push(streamUrl);
    }

    return result;
  }, [channel]);

  const youtubeEmbedUrl = useMemo(
    () => getLiveTvYouTubeEmbedUrl(channel),
    [channel.playbackType, channel.youtubeChannelId, channel.youtubeVideoId],
  );

  const handleStreamFatalError = () => {
    if (youtubeEmbedUrl) {
      setMode('youtube');
      return;
    }

    if (channel.embedUrl) {
      setMode('official_embed');
      return;
    }

    setMode('none');
  };

  if (mode === 'stream' && sources.length > 0) {
    return <LiveTvPlayer sources={sources} poster={channel.logo} onFatalError={handleStreamFatalError} />;
  }

  if (mode === 'youtube' && youtubeEmbedUrl) {
    return (
      <PlayerFrame>
        <iframe
          src={youtubeEmbedUrl}
          title={channel.name}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </PlayerFrame>
    );
  }

  if (mode === 'official_embed' && channel.embedUrl) {
    return (
      <OfficialFrame
        src={channel.embedUrl}
        title={channel.name}
        allow="autoplay"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  }

  return <Unavailable>No playable source available.</Unavailable>;
}

const PlayerFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 8px;
  border-top: 4px solid var(--walky-orange);
  background: #000;
  box-shadow: var(--walky-shadow-strong);

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const OfficialFrame = styled.iframe`
  width: 100%;
  height: 70vh;
  border: 0;
  border-radius: 8px;
  border-top: 4px solid var(--walky-orange);
  background: #000;
  box-shadow: var(--walky-shadow-strong);
`;

const Unavailable = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  box-sizing: border-box;
  border-radius: 8px;
  border-top: 4px solid var(--walky-orange);
  background: var(--walky-charcoal);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.82);
  font-size: 16px;
  text-align: center;
  padding: 24px;
`;
